import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const SIZES = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' } as const;

/**
 * Modal built on Radix Dialog, which handles focus trapping, Escape and the
 * ARIA wiring for free.
 */
export const Dialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: DialogProps) => {
  const { t } = useT();

  return (
  <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 z-40 bg-ink/25" />
      <RadixDialog.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
          'max-h-[calc(100vh-4rem)] overflow-y-auto rounded-xl border border-border bg-surface shadow-xl',
          SIZES[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div>
            <RadixDialog.Title className="text-[15px] font-semibold text-ink">
              {title}
            </RadixDialog.Title>
            {description ? (
              <RadixDialog.Description className="mt-1 text-[13px] text-ink-muted">
                {description}
              </RadixDialog.Description>
            ) : null}
          </div>
          <RadixDialog.Close
            className="rounded-md p-1 text-ink-subtle transition-colors hover:bg-sunken hover:text-ink"
            aria-label={t('common.close')}
          >
            <X className="size-4" />
          </RadixDialog.Close>
        </div>

        <div className="px-5 py-4">{children}</div>

        {/* The footer wraps: a dialog can offer two ways out plus cancel, and
            they must not run past the edge on a narrow screen. */}
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-canvas px-5 py-3">
            {footer}
          </div>
        ) : null}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  </RadixDialog.Root>
  );
};

export const DialogClose = RadixDialog.Close;
